import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { useBusquedaGlobal, type ResultadoDeBusqueda } from './api';

/**
 * La búsqueda de la barra superior: personas, cursos, eventos, publicaciones.
 *
 * ## Los dos escapados que faltaban
 *
 * El armazón armaba los resultados con `innerHTML`, y de todo lo que metía
 * escapaba **solo el título**:
 *
 * ```js
 * No encontramos nada para <strong>"${data.query}"</strong>   // lo que TÚ escribes
 * <div class="gs-item-sub">${it.subtitle || ''}</div>         // cargo, sede, lugar…
 * ```
 *
 * La primera es lo que teclea quien busca, devuelto tal cual. La segunda sale de
 * `u.position or u.department` y de `e.location` — texto que escribe un
 * administrador en una ficha o en un evento, y que le aparece a cualquiera que
 * busque.
 *
 * Aquí no hay escapado que recordar: el texto es texto.
 *
 * ## Lo demás
 *
 * - **La lista se recorre con el teclado de verdad.** Ya se movía con las
 *   flechas, pero era un `<div>` con `.active`: un lector de pantalla no
 *   anunciaba ni cuántos resultados hay ni cuál está marcado. Ahora es un
 *   `combobox` con su `listbox`, que es lo que este control es.
 * - **Deja de pedir por cada tecla.** Tenía su propio `debounceFn` de 250 ms;
 *   ahora es el mismo retardo compartido con el resto del portal, y la lista
 *   anterior se queda mientras llega la nueva en vez de parpadear.
 */
export function BusquedaGlobal() {
  const [texto, setTexto] = useState('');
  const [abierta, setAbierta] = useState(false);
  const [resaltado, setResaltado] = useState(-1);
  const campo = useRef<HTMLInputElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const idLista = useId();

  const consulta = useBusquedaGlobal(useTextoDemorado(texto));
  const grupos = consulta.data?.groups ?? [];
  // La lista plana es la que recorren las flechas: los grupos son rótulos, no
  // destinos.
  const planos: ResultadoDeBusqueda[] = grupos.flatMap((g) => g.items);
  const consultado = consulta.data?.query ?? '';

  // Cerrar al pulsar fuera. El armazón colgaba un listener en `document` por
  // cada apertura y no lo quitaba nunca.
  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierta(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierta]);

  // Los DOS atajos del armazón: `/` y Ctrl/Cmd+K.
  //
  // El segundo estaba suelto en el `init()` de `app.js` y hacía
  // `getElementById('global-search-input')?.focus()`. Con el `?.` y sin ese id en
  // el documento, la combinación habría dejado de hacer nada sin dar error —el
  // mismo defecto que dejó muerto «¿olvidaste tu contraseña?»—, así que viene
  // aquí, junto al campo que enfoca.
  useEffect(() => {
    const atajo = (e: KeyboardEvent) => {
      const dentroDeUnCampo = (e.target as HTMLElement)?.closest('input, textarea, [contenteditable]');
      const esK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (esK || (e.key === '/' && !dentroDeUnCampo)) {
        e.preventDefault();
        campo.current?.focus();
      }
    };
    document.addEventListener('keydown', atajo);
    return () => document.removeEventListener('keydown', atajo);
  }, []);

  function ir(r?: ResultadoDeBusqueda) {
    if (!r) return;
    setAbierta(false);
    setTexto('');
    setResaltado(-1);
    window.APP?.navigate(r.route);
  }

  const hayQueEnsenar = abierta && texto.trim().length >= 2;

  return (
    <div ref={caja} className="relative min-w-0 flex-1 md:max-w-md">
      <span className="relative block">
        <i
          className="bi bi-search pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted"
          aria-hidden="true"
        />
        <input
          ref={campo}
          type="search"
          role="combobox"
          aria-expanded={hayQueEnsenar && planos.length > 0}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-label="Buscar personas, cursos, eventos"
          aria-activedescendant={resaltado >= 0 ? `${idLista}-${resaltado}` : undefined}
          autoComplete="off"
          value={texto}
          placeholder="Buscar personas, cursos, eventos…"
          className="w-full rounded-sm border border-line bg-surface py-2 pl-9 pr-3 text-3 text-ink focus-visible:focus-anillo"
          onChange={(e) => { setTexto(e.target.value); setAbierta(true); setResaltado(-1); }}
          onFocus={() => setAbierta(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setAbierta(false); return; }
            if (!planos.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setResaltado((i) => Math.min(i + 1, planos.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setResaltado((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && resaltado >= 0) {
              e.preventDefault();
              ir(planos[resaltado]);
            }
          }}
        />
      </span>

      {hayQueEnsenar && (
        <div className="absolute inset-x-0 top-full z-40 mt-1 max-h-[70vh] overflow-y-auto rounded-sm border border-line bg-surface shadow-raised">
          {consulta.isPending ? (
            <p className="p-4 text-center text-2 text-muted">Buscando…</p>
          ) : !planos.length ? (
            <p className="p-4 text-center text-3 text-muted">
              <i className="bi bi-search" aria-hidden="true" /> No encontramos nada para{' '}
              {/* Lo que escribió quien busca, como TEXTO. Iba crudo dentro de un
                  `<strong>` en el marcado que esto sustituye. */}
              <strong className="text-ink">«{consultado || texto.trim()}»</strong>
            </p>
          ) : (
            <>
              <ul id={idLista} role="listbox" aria-label="Resultados de la búsqueda">
                {grupos.map((g) => (
                  <li key={g.label}>
                    <p className="flex items-center gap-2 bg-sunken px-3 py-1.5 text-1 font-bold uppercase tracking-wide text-muted">
                      <i className={`bi ${g.icon}`} aria-hidden="true" /> {g.label}
                    </p>
                    <ul>
                      {g.items.map((it) => {
                        const i = planos.indexOf(it);
                        return (
                          <li key={`${it.route}-${it.title}`} role="option" aria-selected={i === resaltado}>
                            <button
                              type="button"
                              id={`${idLista}-${i}`}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2 text-left',
                                i === resaltado ? 'bg-brand/8' : 'hover:bg-sunken',
                              )}
                              onMouseEnter={() => setResaltado(i)}
                              onClick={() => ir(it)}
                            >
                              {it.avatar_url
                                ? <Avatar nombre={it.title} foto={it.avatar_url} tamano={28} />
                                : (
                                  <span className="flex size-7 shrink-0 items-center justify-center rounded-xs bg-sunken text-muted">
                                    <i className={`bi ${g.icon}`} aria-hidden="true" />
                                  </span>
                                )}
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-3 text-ink">{it.title}</span>
                                {it.subtitle && (
                                  <span className="block truncate text-2 text-muted">{it.subtitle}</span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
              <p className="flex flex-wrap gap-3 border-t border-line px-3 py-2 text-1 text-muted">
                <span><kbd>↑</kbd> <kbd>↓</kbd> navegar</span>
                <span><kbd>↵</kbd> abrir</span>
                <span><kbd>esc</kbd> cerrar</span>
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** El texto, pero solo cuando quien escribe se detiene. Los mismos 250 ms que
 *  tenía el `debounceFn` propio del armazón. */
function useTextoDemorado(texto: string, ms = 250): string {
  const [valor, setValor] = useState(texto);
  useEffect(() => {
    const t = window.setTimeout(() => setValor(texto), ms);
    return () => window.clearTimeout(t);
  }, [texto, ms]);
  return valor;
}
